import useApproveHoneyContractCall from '../hooks/bc/useApproveHoneyContractCall';
import useConnectedAccount from '../hooks/useConnectedAccount';
import useDressingRoomContext from '../hooks/useDressingRoomContext';
import useDynamicNftContext from '../hooks/useDynamicNftContext';
import useHoneyTraitsSaleAllowanceContractCall from '../hooks/bc/useHoneyTraitsSaleAllowanceContractCall';
import usePurchaseFormValuesSumUtil from '../hooks/usePurchaseFormValuesSumUtil';
import useStoreContext from '../hooks/useStoreContext';
import useStoreSelectedTraitsTotalPriceUtil from '../hooks/useStoreSelectedTraitsTotalPriceUtil';
import useTraitCollectionContext from '../hooks/useTraitCollectionContext';
import useTranslate from '../hooks/useTranslate';
import useV2TraitsPurchaseWithHoneyContractCall from '../hooks/bc/useV2TraitsPurchaseWithHoneyContractCall';
import V2TraitsHoneyPurchaseFormContainer from './V2TraitsHoneyPurchaseFormContainer';
import { Box, Button } from '@chakra-ui/react';
import { constants } from 'ethers';
import { Currency } from '../types';
import { formatEther } from 'ethers/lib/utils';
import { formatEtherBalance } from '../utils/numberUtils';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { HoneyCheckoutFormValues } from '../types/purchaseValues';
import { useCallback, useMemo, useState } from 'react';

type V2TraitsHoneyCheckoutContainerProps = {
  onSuccess: () => void;
};

const V2TraitsHoneyCheckoutContainer = ({
  onSuccess,
}: V2TraitsHoneyCheckoutContainerProps) => {
  const translate = useTranslate();
  const [walletAddress] = useConnectedAccount();
  const { traitCollection } = useTraitCollectionContext();
  const { traitSalesByTraitId } = useStoreContext();
  const { changedTraitIds } = useDressingRoomContext();
  const { dynamicNft } = useDynamicNftContext();
  const [allowance, readAllowance] =
    useHoneyTraitsSaleAllowanceContractCall(walletAddress);

  const [selectedTokenId, setSelectedTokenId] = useState<number | undefined>(
    dynamicNft.tokenId
  );

  const [formValues, setFormValues] = useState<HoneyCheckoutFormValues>({});

  const totalPrice = useStoreSelectedTraitsTotalPriceUtil(changedTraitIds);

  const formValuesAmountSum = usePurchaseFormValuesSumUtil(formValues);

  const totalPercentage = totalPrice.eq('0')
    ? 100
    : (parseInt(formatEther(formValuesAmountSum)) /
        parseInt(formatEther(totalPrice))) *
      100;

  const isCheckingAllowance = allowance === undefined;
  const isFree = totalPrice.eq('0');
  const isTotalAmount = formValuesAmountSum.eq(totalPrice);

  const areEnoughAmountApproved = useMemo(() => {
    if (!formValues.wallet?.amount || formValues.wallet.amount.isZero?.()) {
      return true;
    }
    return allowance?.gte(formValues.wallet.amount) || false;
  }, [allowance, formValues.wallet?.amount]);

  const traitTokenIds = useMemo(
    () =>
      changedTraitIds?.map(traitId => traitSalesByTraitId[traitId].tokenId) ||
      [],
    [changedTraitIds, traitSalesByTraitId]
  );

  const [purchase, isPurchasing] = useV2TraitsPurchaseWithHoneyContractCall(
    selectedTokenId,
    traitTokenIds,
    formValues,
    onSuccess
  );

  const [approve, isApproving] = useApproveHoneyContractCall(
    traitCollection.saleContractAddress,
    constants.MaxUint256,
    readAllowance
  );

  const handleDynamicNftChange = useCallback((tokenId?: number) => {
    setSelectedTokenId(tokenId);
  }, []);

  const handleValuesChange = useCallback(
    (formValues: HoneyCheckoutFormValues) => {
      setFormValues(formValues);
    },
    []
  );

  return (
    <>
      <V2TraitsHoneyPurchaseFormContainer
        selectedTokenId={selectedTokenId}
        values={formValues}
        onValuesChange={handleValuesChange}
        onDynamicNftChange={handleDynamicNftChange}
      />

      <Box
        position="relative"
        mt={isFree ? '4' : '6'}
        borderRadius="md"
        overflow="hidden"
      >
        {isCheckingAllowance ? (
          <Button w="full" colorScheme="dark" isLoading />
        ) : (
          <>
            {!areEnoughAmountApproved ? (
              <Button
                w="full"
                onClick={() => approve()}
                isLoading={isApproving}
              >
                {translate('purchase:approve')}
              </Button>
            ) : (
              <>
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  bottom="0"
                  width={`${totalPercentage}%`}
                  bg="primary.500"
                  transition="all 0.15s"
                />

                <Button
                  w="full"
                  colorScheme="primary"
                  onClick={() => purchase()}
                  isDisabled={!isTotalAmount || !areEnoughAmountApproved}
                  isLoading={isPurchasing}
                >
                  {translate(
                    isFree ? 'purchase:submit:free' : 'purchase:submit',
                    {
                      price: formatEtherBalance(totalPrice),
                      currency: getCurrencySymbol(Currency.Honey),
                    }
                  )}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default V2TraitsHoneyCheckoutContainer;
